<template>
  <div id="create-article">
    <div class="top-bar">
      <div class="d-none d-lg-block col py-0"></div>
      <div class="page-title m-3">
        <h2 v-if="!article.id" class="d-inline-block">
          {{
            $store.state.user.langEng
              ? "Create&nbsp;article"
              : "Skapa&nbsp;artikel"
          }}
          <v-icon>add</v-icon>
        </h2>
        <h2 v-else class="d-inline-block">
          {{
            $store.state.user.langEng
              ? "Edit&nbsp;article"
              : "Redigera&nbsp;artikel"
          }}
          <v-icon>create</v-icon>
        </h2>
      </div>
    </div>

    <div class="page-title-buttons d-flex justify-center">
      <v-btn
        class="mr-1 m-1"
        small
        color="grey lighten-3"
        @click="toggleCodeFromZipButton"
      >
        <v-icon>attach_file</v-icon>
        {{
          $store.state.user.langEng
            ? isSelectingZip
              ? "Cancel listing"
              : "Upload file"
            : isSelectingZip
            ? "Avbryt listning"
            : "Ladda upp fil"
        }}
      </v-btn>

      <v-btn
        v-if="article.id"
        small
        class="m-1"
        color="grey lighten-3"
        @click="displayDuplicateDialog = true"
      >
        <v-icon>file_copy</v-icon>
        {{ $store.state.user.langEng ? "Duplicate" : "Duplicera" }}
      </v-btn>

      <v-btn
        small
        class="m-1"
        color="grey lighten-3"
        @click="togglePreviewButton"
      >
        <v-icon>{{ isPreviewing ? "edit" : "launch" }}</v-icon>
        {{
          $store.state.user.langEng
            ? isPreviewing
              ? "Edit"
              : "Preview"
            : isPreviewing
            ? "Redigera"
            : "Förhandsgranska"
        }}
      </v-btn>

      <v-btn
        @click="displayClearDialog = true"
        small
        class="m-1"
        color="grey lighten-1"
        active-class
      >
        <v-icon>clear</v-icon>
        {{ $store.state.user.langEng ? "Clear" : "Rensa" }}
      </v-btn>
    </div>
    <article-time-picker
      @newDateTime="newDateTime($event)"
      :setTime="article.publishedAt"
      :updateTime="updateTime"
      ref="refView"
    />
    <v-dialog v-model="displayClearDialog" max-width="180px">
      <v-card>
        <v-card-actions>
          <v-btn text @click="displayClearDialog = false">{{
            $store.state.user.langEng ? "Cancel" : "Avbryt"
          }}</v-btn>
          <v-btn color="red lighten-2" @click="clearContent">{{
            $store.state.user.langEng ? "Clear" : "Rensa"
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="displayDuplicateDialog" max-width="220px">
      <v-card>
        <v-card-actions>
          <v-btn text @click="displayDuplicateDialog = false">{{
            $store.state.langEng ? "Cancel" : "Avbryt"
          }}</v-btn>
          <v-btn color="red lighten-2" @click="duplicateArticle">{{
            $store.state.langEng ? "Duplicate" : "Duplicera"
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <transition-group
      name="animate-route"
      mode="out-in"
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
    >
      <article-page
        v-if="currentContentArea === 'PreviewArticle'"
        id="preview-article"
        key="preview-article"
        :previewArticle="article"
      />
      <code-from-zip
        v-if="currentContentArea === 'CodeFromZip'"
        key="code-from-zip"
      />
      <create-article-textarea
        v-show="currentContentArea === 'TextArea'"
        key="create-article-textarea"
      />
    </transition-group>
  </div>
</template>

<script>
import { Vue, Component, Watch } from "vue-property-decorator";
import CreateArticleTextarea from "@/components/CreateArticle/CreateArticleTextarea";
import ArticleTimePicker from "@/components/CreateArticle/ArticleTimePicker";
import ArticlePage from "@/views/ArticlePage";
import CodeFromZip from "@/components/CreateArticle/FileUpload/CodeFromZip";
import marked from "marked";
import { eventBus } from "@/main";
import { Rest } from "@/utils";

@Component({
  components: {
    ArticleTimePicker,
    CreateArticleTextarea,
    ArticlePage,
    CodeFromZip,
  },
})
export default class CreateArticle extends Vue {
  updateTime = false;
  articleId = "";
  togglePreview = false;
  displayClearDialog = false;
  displayDuplicateDialog = false;
  resetCachedArticle = false;

  get article() {
    return this.$store.state.article.currentArticle;
  }

  get isSelectingZip() {
    return this.currentContentArea === "CodeFromZip";
  }

  get isPreviewing() {
    return this.currentContentArea === "PreviewArticle";
  }

  get currentContentArea() {
    return this.$store.state.article.currentContentArea;
  }

  get slugError() {
    return this.$store.state.article.slugError;
  }

  get user() {
    return this.$store.state.user.loggedInUser;
  }

  set currentContentArea(value) {
    this.$store.commit("setCurrentContentArea", value);
  }

  resetPrivateFields() {
    this.updateTime = false;
    this.togglePreview = false;
    this.displayClearDialog = false;
    this.currentContentArea = "TextArea";
  }

  @Watch("$route.params.slug")
  async watchSlug(slug) {
    if (slug) {
      this.onCreated();
    }
  }

  @Watch("$store.state.article.currentArticle", { deep: true })
  watchArticle(newVal = {}) {
    if (this.initLocalStorage) {
      localStorage.setItem("temp-article", JSON.stringify(newVal));
    }

    this.initLocalStorage = true;
  }

  @Watch("$store.state.article.buttonAction")
  onButtonClick(newVal) {
    if (newVal && (this.slugError || this.article.slug == "")) {
      this.$store.commit("setSlugError", true);
    } else if (newVal && (this.titleError || this.article.title == "")) {
      this.$store.commit("setTitleError", true);
    } else if (newVal && (this.contentError || this.article.content == "")) {
      this.currentContentArea = "TextArea";
      this.$store.commit("toggleCreateArticleList", false);
      this.$store.commit("setContentError", true);
    } else if (newVal) {
      if (newVal != "delete") {
        this.article.deleted = false;
        const slug = this.article.slug.replace(/-deleted\d*$/, "");
        this.article.slug = slug;
        this.$store.commit("forceNewTitleAndSlug", {
          title: this.article.title,
          slug,
        });
      }
      this[newVal]();
    }
  }

  @Watch("$store.state.article.createArticleSlugToLoad")
  onClickArticleItem(value) {
    if (value) {
      this.onCreated();
      this.article.title = value.title;
      this.article.slug = value.slug;
      setTimeout(() => {
        this.$store.commit("setCreateArticleSlugToLoad");
      }, 50);
    }
  }

  async save() {
    if (this.article.id) {
      await this.put();
    } else {
      await this.post();
    }
    this.goToPublishedArticle();
  }

  async saveDraft() {
    this.article.publishedAt = 0;
    if (this.article.id) {
      await this.put();
    } else {
      await this.post();
    }

    // send new random number to trigger update in child
    this.updateTime = !this.updateTime;
    this.$store.commit("toggleSnackbar", {
      text: this.$store.state.user.langEng
        ? "Saved as draft"
        : "Sparad som utkast",
    });
  }

  async publish() {
    if (this.article.id) {
      await this.put();
    } else {
      await this.post();
    }

    this.goToPublishedArticle();
  }

  goToPublishedArticle() {
    this.resetCachedArticle = true;

    const slug = this.article.slug;
    this.clearContent(false);
    this.$store.commit("resetSearch");

    this.$router.push({
      name: "article",
      params: { slug },
    });

    setTimeout(() => {
      if (location.pathname.includes("create")) {
        history.replaceState({}, null, "/article/" + slug);
      }
    }, 500);
  }

  async unpublish() {
    this.article.publishedAt = 0;
    this.article.deleted = false;
    await this.put();
  }

  async delete() {
    // Should only do "deleted" if there are 0 klasses using it.
    this.article.deleted = true;
    let deletedName = this.article.slug + "-deleted";

    for (let attempt = 2; attempt < 100; ++attempt) {
      const deletedArticle = await Rest.get("Article", {
        properties: { slug: deletedName },
      });
      if (deletedArticle.length) {
        deletedName = this.article.slug + "-deleted" + attempt;
      } else {
        break;
      }
    }

    this.article.slug = deletedName;
    await this.put();
    this.clearContent(true);
    this.$store.commit("toggleSnackbar", {
      text: this.$store.state.user.langEng
        ? 'Deleted. Navigate "Back" to get back to the article.'
        : 'Raderad. Gå "Bakåt" för att återgå till artikeln.',
    });
  }

  async detach() {
    let klassInArray = this.article.klasses.find(
      (k) => k.slug == this.$store.getters.classNameInUrl
    );
    if (!klassInArray) {
      klassInArray = this.$store.state.klasses.find(
        (k) => k.slug == this.$store.getters.classNameInUrl
      );
      this.article.klasses.push(klassInArray);
    }
    klassInArray.detach = true;
    await this.put();
  }

  preDB() {
    if (this.article.publishedAt.getTime) {
      this.article.publishedAt = this.article.publishedAt.getTime();
    }
    this.article.snippet = this.articleSnippet();
    this.article.klass = this.$store.getters.classNameInUrl;
    this.article.author = this.user;
  }

  postDB(dbResult) {
    history.replaceState({}, null, "/create-article/" + this.article.slug);
    this.$store.commit("setCurrentArticle", dbResult);
    // this.onCreated();
  }

  async put() {
    this.preDB();
    // console.log("Before PUT:", { ...this.article });
    const res = await Rest.put("Article", this.article).catch(console.error);
    // console.log("After PUT:", { ...res });
    this.postDB(res);
  }

  async post() {
    this.preDB();
    // console.log("Before POST:", { ...this.article });
    const res = await Rest.post("Article", this.article).catch(console.error);
    // console.log("After POST:", { ...res });
    this.postDB(res);
  }

  togglePreviewButton() {
    if (!this.article.content && this.currentContentArea !== "PreviewArticle")
      return;
    this.currentContentArea =
      this.currentContentArea === "PreviewArticle"
        ? "TextArea"
        : "PreviewArticle";
  }

  toggleCodeFromZipButton() {
    this.currentContentArea =
      this.currentContentArea === "CodeFromZip" ? "TextArea" : "CodeFromZip";
  }

  async duplicateArticle() {
    this.$refs.refView.onInit = false;

    this.displayDuplicateDialog = false;

    let dbArticle = await Rest.get("Article", {
      properties: { slug: this.$route.params.slug },
      eager: { categories: true },
    });
    dbArticle = dbArticle[0];

    delete dbArticle.id;
    delete dbArticle._id;
    delete dbArticle.createdAt;
    dbArticle.slug = "";
    dbArticle.publishedAt = this.lockTime(Date.now()).getTime();
    dbArticle.deleted = false;
    dbArticle.pinned = false;
    dbArticle.content = this.article.content;
    history.replaceState({}, null, "/create-article/");

    this.$store.commit("setCurrentArticle", dbArticle);
    localStorage.setItem("temp-article", JSON.stringify(dbArticle));
    setTimeout(() => {
      this.$store.commit("forceNewTitleAndSlug", {
        title: dbArticle.title,
        slug: "",
        init: true,
      });
    }, 10);
  }

  clearContent(resetUrl = true) {
    localStorage.removeItem("temp-article");
    if (resetUrl && location.pathname != "/create-article") {
      this.$route.params.slug && this.$router.push("/create-article/");
      !this.$route.params.slug &&
        history.replaceState({}, null, "/create-article");
    }
    this.$store.commit("setCreateArticleSlugToLoad");
    this.$store.commit("resetCurrentArticle");
    eventBus.$emit("clear-selected-categories");
    setTimeout(() => {
      this.$store.commit("forceNewTitleAndSlug", { title: "", slug: "" });
    }, 10);
    this.resetPrivateFields();

    setTimeout(() => {
      this.$store.commit("setSlugError", false);
      this.$store.commit("setTitleError", false);
      this.$store.commit("setContentError", false);
    }, 10);
  }

  articleSnippet() {
    let div = document.createElement("div");
    div.innerHTML = marked(this.article.content.substr(0, 300));

    return div.innerText.replace(/\n/gm, " ").trim();
  }

  newDateTime(time) {
    this.article.publishedAt = time;
  }

  async onCreated() {
    this.resetPrivateFields();

    let dbArticle =
      JSON.parse(localStorage.getItem("temp-article")) || this.article;

    if (this.$route.params.slug) {
      if (dbArticle.slug != this.$route.params.slug || !dbArticle.id) {
        dbArticle = await Rest.get("Article", {
          properties: { slug: this.$route.params.slug },
          eager: { categories: true, klasses: true },
        });
        dbArticle = dbArticle[0];
      }
    }

    setTimeout(() => {
      dbArticle &&
        this.$store.commit("forceNewTitleAndSlug", {
          title: dbArticle.title,
          slug: dbArticle.slug,
          init: true,
        });
    }, 10);

    if (dbArticle) {
      dbArticle.id &&
        history.replaceState({}, null, "/create-article/" + dbArticle.slug);
      this.$store.commit("toggleCreateArticleList", false);
      this.$store.commit("setCurrentArticle", dbArticle);
      setTimeout(() => {
        eventBus.$emit("init-edit-categories", dbArticle.categories);
      }, 0);
    } else {
      this.$store.commit("resetCurrentArticle");
    }

    setTimeout(() => {
      this.article.publishedAt = this.lockTime();
    }, 0);
  }

  lockTime(time) {
    let today = new Date(time || this.article.publishedAt || Date.now());
    let m = today.getMinutes();
    today.setMinutes(m - (m % 5));
    return today;
  }

  created() {
    this.onCreated();
  }

  mounted() {
    this.$store.commit("setCurrentKlass", "");
  }

  beforeDestroy() {
    this.resetCachedArticle && localStorage.removeItem("temp-article");
  }
}
</script>

<style lang="scss">
#create-article {
  .top-bar {
    display: grid;
    grid-template-columns: 1fr;
    .page-title {
      grid-column: 1;
      grid-row: 1;
      justify-self: center;
      h2 {
        font-size: 2em;
      }
    }
  }
}
.page-title-buttons {
  grid-column: 1;
  grid-row: 1;
}
#text-fields,
#preview-article,
#article-text,
#article-list {
  animation-duration: 150ms;
}
#create-article {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  .v-text-field {
    max-height: 50px;
    .v-input__slot {
      border-radius: 5px;
    }
    .v-text-field__details {
      display: none;
    }
  }
  #title-fields {
    display: flex;
    flex-direction: row;
    width: 100%;
    .input-title {
      margin-right: 1%;
    }
    .article-slug {
      max-width: 35%;
    }
  }
  #article-text {
    height: 75vh;
    overflow-y: auto;
    position: relative;
    border-radius: 5px;
    cursor: text;
    .text-content {
      position: absolute;
      width: 100%;
      height: 100% !important;
      textarea {
        font-family: "Consolas", "Liberation", "Mono", "Menlo", Courier,
          monospace;
        font-size: 16px;
        line-height: 1.618;
        letter-spacing: 0.015em;
        color: #000000;
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: subpixel-antialiased;
      }
      .v-messages {
        display: none;
      }
      .v-text-field__details {
        display: none;
      }
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }
}

.mobile {
  #create-article {
    padding: 0 15px;
    .page-title-buttons {
      flex-wrap: wrap;
    }
  }
}
</style>
